/* Mainola live-theme enhancements.
   1. Scroll reveal: text and images below the first screen fade and rise in as they are reached.
   2. Floating "Check availability" button on the product page and the home page.
   3. Bridge between the custom product form (#nkForm) and Horizon's native cart drawer,
      so "Add to cart" opens the drawer (theme setting "auto_open_cart_drawer"), with a
      fallback to the cart page when the drawer cannot open.
   Never runs in the theme editor. With prefers-reduced-motion the reveal is a short fade only. */

const designMode = Boolean(window.Shopify && window.Shopify.designMode);
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------ */
/* 1. Scroll reveal                                                    */
/* ------------------------------------------------------------------ */
/* Reveal units are found by walking each section from the top: a node becomes one unit when it is
   "atomic" (text, media, card, app block, horizontal scroller) or small enough (<= ~45% of the
   screen); larger wrappers, grids and lists are descended so their children cascade in.
   Only units that start below the first screen are hidden, and only by JS (no-JS = all visible). */
const REVEAL_ATOMIC =
  'h1,h2,h3,h4,h5,h6,p,li,dt,dd,img,picture,video,svg,canvas,iframe,figure,table,blockquote,details,' +
  'button,input,select,textarea,label,form,a,[class*="card"],.nk-slides,.nk-thumbs,.shopify-app-block,' +
  '[id^="looxReviews"],.loox-reviews-default';
const REVEAL_SKIP =
  '[data-nk-no-reveal],.nk-sticky,#nkSticky2,.nk-lb,.nkfab,dialog,[popover],script,style,template,noscript,link,meta';
const REVEAL_DUR = reduceMotion ? 450 : 850; // keep in sync with nk-live.css
const REVEAL_STEP = reduceMotion ? 50 : 110; // cascade step between units revealed together
const REVEAL_MAX_STEPS = 5;

function initReveal() {
  if (!('IntersectionObserver' in window)) return;
  // Back/forward navigation restores an old scroll position, possibly after we run: skip to avoid a flash.
  const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (nav && /** @type {PerformanceNavigationTiming} */ (nav).type === 'back_forward') return;

  const main = document.getElementById('MainContent');
  if (!main) return;
  const roots = [main];
  const footer = document.querySelector('.page-wrapper > footer') || document.querySelector('footer');
  if (footer && !main.contains(footer)) roots.push(footer);

  const vh = window.innerHeight;
  const maxUnit = vh * 0.45;
  /** @type {HTMLElement[]} */
  const picked = [];

  /** @param {Element} node */
  function hasOwnText(node) {
    for (const c of node.childNodes) if (c.nodeType === 3 && c.textContent && c.textContent.trim()) return true;
    return false;
  }

  /** @param {Element} node @param {CSSStyleDeclaration} cs */
  function isRow(node, cs) {
    const kids = [...node.children].filter((k) => k.getBoundingClientRect().height > 0);
    if (kids.length < 2 || kids.length > 12) return false;
    const avg = kids.reduce((s, k) => s + k.getBoundingClientRect().height, 0) / kids.length;
    if (avg < 60) return false; // chips, badges, icons: keep together
    if (cs.display.includes('grid')) return true;
    if (cs.display.includes('flex') && !cs.flexDirection.startsWith('column')) return true;
    return node.tagName === 'UL' || node.tagName === 'OL';
  }

  /** @param {Element} node */
  function walk(node) {
    if (!(node instanceof HTMLElement || node instanceof SVGElement)) return;
    if (node.matches(REVEAL_SKIP)) return;
    const cs = getComputedStyle(node);
    if (cs.display === 'none' || cs.visibility === 'hidden') return;
    if (cs.position === 'fixed' || cs.position === 'sticky') return;
    if (cs.display === 'contents') {
      [...node.children].forEach(walk);
      return;
    }
    const r = node.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;

    const atomic =
      node.matches(REVEAL_ATOMIC) ||
      hasOwnText(node) ||
      node.children.length === 0 ||
      (/(auto|scroll|hidden)/.test(cs.overflowX) && node.scrollWidth > node.clientWidth + 1);

    if (r.top < vh) {
      // Starts on the first screen (or above it): never hidden. Look inside for lower parts.
      if (!atomic) [...node.children].forEach(walk);
      return;
    }
    if (atomic || (r.height <= maxUnit && !isRow(node, cs))) {
      picked.push(/** @type {HTMLElement} */ (node));
      return;
    }
    [...node.children].forEach(walk);
  }

  roots.forEach((root) => [...root.children].forEach(walk));
  if (!picked.length) return;

  /** @param {HTMLElement} el */
  const done = (el) => {
    el.classList.remove('nkr', 'nkr-in');
    el.style.removeProperty('--nkr-d');
  };

  const io = new IntersectionObserver(
    (entries) => {
      const shown = entries
        .filter((e) => e.isIntersecting)
        .map((e) => /** @type {HTMLElement} */ (e.target))
        .sort((a, b) => {
          const ra = a.getBoundingClientRect();
          const rb = b.getBoundingClientRect();
          return Math.abs(ra.top - rb.top) > 24 ? ra.top - rb.top : ra.left - rb.left;
        });
      shown.forEach((el, i) => {
        io.unobserve(el);
        const delay = Math.min(i, REVEAL_MAX_STEPS) * REVEAL_STEP;
        el.style.setProperty('--nkr-d', delay + 'ms');
        el.classList.add('nkr-in');
        // Hand the element back to its own styles (hover transitions etc.) once it has arrived.
        setTimeout(() => done(el), delay + REVEAL_DUR + 150);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0 }
  );

  picked.forEach((el) => {
    el.classList.add('nkr');
    io.observe(el);
  });

  // Safety net: anything still hidden when printing is shown.
  window.addEventListener('beforeprint', () => picked.forEach(done));
}

/* ------------------------------------------------------------------ */
/* 2. Floating "Check availability" button                             */
/* ------------------------------------------------------------------ */
/* Product page: follows the scroll whenever the buy buttons are off screen and scrolls back to them
   (arrow points up or down towards them). Home page: same pill, shown once the hero button has
   scrolled away, links to the product page's buy zone (#nkbuy). Never shown while the buy buttons
   (or, on the home page, a "Shop" button) are on screen, while a drawer is open, or behind a large
   cookie panel. Shown whatever the stock state: the target is the form's own button (Add to cart or
   Sold out). */
const PRODUCT_PATH = '/products/gel-support-cushion';
const BUY_HASH = '#nkbuy';

/** Current scroll offset: the window, or .page-wrapper when it is the scroller (desktop drawer). */
function scrollPos() {
  const w = document.querySelector('.page-wrapper');
  return Math.max(window.scrollY || 0, w ? w.scrollTop : 0);
}

/** @param {HTMLElement} target */
function scrollToBuy(target, instant) {
  const r = target.getBoundingClientRect();
  // Land with the offer cards above and both buttons in the lower half of the screen.
  const offset = r.top - window.innerHeight * 0.55;
  const behavior = instant || reduceMotion ? 'auto' : 'smooth';
  const wrapper = document.querySelector('.page-wrapper');
  const wrapperScrolls =
    wrapper &&
    wrapper.scrollHeight > wrapper.clientHeight + 1 &&
    /(auto|scroll)/.test(getComputedStyle(wrapper).overflowY);
  if (wrapperScrolls) {
    wrapper.scrollTo({ top: Math.max(0, wrapper.scrollTop + offset), behavior });
  } else {
    window.scrollTo({ top: Math.max(0, window.scrollY + offset), behavior });
  }
  target.focus({ preventScroll: true });
}

function initFloatingCta() {
  if (!('IntersectionObserver' in window)) return;
  // Product page: #nkAdd when in stock; the form's own (sold out) button otherwise.
  const addBtn = document.getElementById('nkAdd') || document.querySelector('#nkForm button[type=submit], #nkForm button');
  const isHome = !addBtn && Boolean(document.querySelector('meta[name="nk-template"][content="index"]'));
  /** @type {HTMLElement[]} */
  let buyEls;
  if (addBtn) {
    buyEls = [addBtn, document.getElementById('nkBuyNow')].filter(Boolean);
  } else if (isHome) {
    buyEls = [...document.querySelectorAll('#MainContent a.btn[href*="' + PRODUCT_PATH + '"]')];
    if (!buyEls.length) return;
  } else {
    return;
  }

  // Arrive from the home pill (or any link to #nkbuy): bring the buy buttons into view.
  if (addBtn && location.hash === BUY_HASH) {
    const go = () => scrollToBuy(addBtn, true);
    if (document.readyState === 'complete') requestAnimationFrame(go);
    else window.addEventListener('load', () => requestAnimationFrame(go), { once: true });
    try {
      history.replaceState(history.state, '', location.pathname + location.search);
    } catch (e) {}
  }

  const btn = document.createElement(isHome ? 'a' : 'button');
  if (btn instanceof HTMLAnchorElement) btn.href = PRODUCT_PATH + BUY_HASH;
  else btn.type = 'button';
  btn.className = 'nkfab';
  btn.setAttribute('aria-hidden', 'true');
  btn.tabIndex = -1;
  btn.innerHTML =
    '<span>Check availability</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
  document.body.appendChild(btn);
  document.documentElement.classList.add('nkfab-ready');

  const visible = new Map();
  let bannerBlocks = false;

  // Footer: the button goes away as soon as the top of the footer enters the screen. Horizon renders
  // the footer group as <footer> inside .page-wrapper; other selectors cover renamed sections. The
  // rect is viewport-relative, so it works whether the window or .page-wrapper (desktop) scrolls.
  const main = document.getElementById('MainContent');
  const footers = [
    ...document.querySelectorAll(
      '.page-wrapper > footer, footer.shopify-section-group-footer-group, #shopify-section-footer, .shopify-section-group-footer-group, body footer'
    ),
  ].filter((el, i, all) => all.indexOf(el) === i && !(main && main.contains(el)) && !el.closest('.nkfab'));
  function footerIn() {
    const vh = window.innerHeight;
    return footers.some((el) => {
      const r = el.getBoundingClientRect();
      return r.height > 0 && r.top < vh && r.bottom > 0;
    });
  }

  function buyState() {
    let anyIn = false;
    let allAbove = true;
    const vh = window.innerHeight;
    buyEls.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return; // hidden (e.g. mobile-only / desktop-only copy)
      if (visible.get(el) || (r.bottom > 0 && r.top < vh)) anyIn = true;
      if (r.bottom > 0) allAbove = false;
    });
    return { anyIn, allAbove };
  }

  function update() {
    const { anyIn, allAbove } = buyState();
    const drawerOpen = Boolean(document.querySelector('theme-drawer[open]'));
    // Product page: also shown before the buy zone (arrow down) once the shopper has started scrolling.
    // Home page: only after the hero button, so it never competes with it on the first screen.
    const placed = isHome
      ? buyEls[0].getBoundingClientRect().bottom < 0
      : allAbove || scrollPos() > window.innerHeight * 0.35;
    const on = !anyIn && placed && !bannerBlocks && !drawerOpen && !footerIn();
    btn.classList.toggle('is-on', on);
    btn.classList.toggle('is-down', !isHome && !allAbove);
    btn.setAttribute('aria-hidden', on ? 'false' : 'true');
    btn.tabIndex = on ? 0 : -1;
  }

  const buyIo = new IntersectionObserver((entries) => {
    entries.forEach((e) => visible.set(e.target, e.isIntersecting));
    update();
  });
  buyEls.forEach((el) => buyIo.observe(el));
  // Footer entering / leaving the screen (window or .page-wrapper scroll): re-check right away.
  if (footers.length) {
    const footIo = new IntersectionObserver(() => update());
    footers.forEach((el) => footIo.observe(el));
  }

  // Hide while any theme drawer (cart, menu) is open.
  document.querySelectorAll('theme-drawer').forEach((d) =>
    new MutationObserver(update).observe(d, { attributes: true, attributeFilter: ['open'] })
  );

  // Cheap rAF-throttled re-check on scroll, as a backstop for large jumps (anchors, scroll restore).
  // Capture phase so it also hears .page-wrapper, which becomes the scroller on desktop while the
  // cart drawer is open in "squeeze" mode.
  let ticking = false;
  document.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    },
    { passive: true, capture: true }
  );

  // Keep clear of the Shopify cookie / privacy banner (shown to UK/EU visitors until they answer):
  // the button is centred, so whenever the banner covers its column it sits just above it. On phones
  // the banner takes over half the screen (about 480 px of 844 on an iPhone), so the button still
  // sits above it and is hidden only when there is no room left for it under the header.
  function lift() {
    const banner = document.getElementById('shopify-pc__banner');
    let h = 0;
    if (banner) {
      const r = banner.getBoundingClientRect();
      const cs = getComputedStyle(banner);
      const b = btn.getBoundingClientRect();
      const overlapsX = r.left < b.right + 8 && r.right > b.left - 8;
      if (overlapsX && cs.display !== 'none' && cs.visibility !== 'hidden' && r.height > 0 && r.top < window.innerHeight) {
        h = Math.max(0, window.innerHeight - r.top);
      }
    }
    const blocked = h > 0 && window.innerHeight - h < 140;
    if (blocked !== bannerBlocks) {
      bannerBlocks = blocked;
      update();
    }
    btn.style.setProperty('--nkfab-lift', (blocked ? 0 : h) + 'px');
  }
  lift();
  new MutationObserver(lift).observe(document.body, { childList: true, subtree: false, attributes: false });
  window.addEventListener('resize', lift, { passive: true });
  setInterval(lift, 1500);

  if (addBtn) btn.addEventListener('click', () => scrollToBuy(addBtn, false));

  update();
}

/* ------------------------------------------------------------------ */
/* 3. Custom product form -> native Horizon cart drawer                */
/* ------------------------------------------------------------------ */
function initCartBridge() {
  const form = document.getElementById('nkForm');
  if (!form || !document.querySelector('cart-drawer-component')) return;

  // The drawer listens for Shopify's standard CartLinesUpdateEvent. The module is usually loaded
  // already (the theme imports it); if it is slow we wait a little, and if it cannot load at all
  // (content blocker, network) we still add, then send the shopper to the cart page so the add is
  // never silent.
  /** @type {Promise<any>} */
  const eventsReady = import('@shopify/events').catch(() => null);
  /** @param {number} ms */
  const withTimeout = (ms) => Promise.race([eventsReady, new Promise((r) => setTimeout(() => r(null), ms))]);

  // Capture phase on document runs before the form's own inline submit handler.
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target !== form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      withTimeout(2500).then((m) => (m && m.CartLinesUpdateEvent ? addToCart(m) : addWithoutDrawer()));
    },
    true
  );

  let busy = false;

  async function addWithoutDrawer() {
    if (busy) return;
    busy = true;
    const btn = document.getElementById('nkAdd');
    const err = document.getElementById('nkErr');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Unable to add to cart');
      window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart';
    } catch (e) {
      busy = false;
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Add to cart';
      }
      if (err) {
        err.textContent = e instanceof Error ? e.message : 'Unable to add to cart';
        err.style.display = 'block';
      }
    }
  }

  async function addToCart({ CartLinesUpdateEvent }) {
    if (busy) return;
    busy = true;
    const btn = document.getElementById('nkAdd');
    const err = document.getElementById('nkErr');
    const label = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
      btn.style.opacity = '.75';
    }
    if (err) err.style.display = 'none';

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));
    const quantity = Number(formData.get('quantity')) || 1;

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    const reset = () => {
      busy = false;
      if (!btn) return;
      btn.disabled = false;
      btn.textContent = label;
      btn.style.opacity = '';
    };

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

      if (!res.ok || data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'nk-form', itemCount: quantity },
        });
        throw new Error((data && (data.description || data.message)) || 'Unable to add to cart');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'nk-form',
          itemCount: quantity,
          sections: data.sections,
        },
      });

      if (btn) btn.textContent = 'Added ✓';
      setTimeout(reset, 1600);

      // Safety net: if the drawer did not open (its script failed or is not upgraded), open it
      // directly, or fall back to the cart page, so the shopper always sees the result.
      setTimeout(() => {
        if (document.querySelector('theme-drawer[open]')) return;
        const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
        const host = drawer && drawer.closest('theme-drawer');
        if (host && customElements.get('cart-drawer-component') && typeof host.open === 'function') host.open();
        else window.location.href = '/cart';
      }, 900);
    } catch (e) {
      deferred.reject(e); // no-op if already resolved
      reset();
      if (err) {
        err.textContent = e instanceof Error ? e.message : 'Unable to add to cart';
        err.style.display = 'block';
      }
    }
  }
}

if (!designMode) {
  const run = () => {
    initReveal();
    initFloatingCta();
    initCartBridge();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
}
